const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getJsonHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export type BodyPart =
  | 'FRONT'
  | 'BACK'
  | 'LEFT_SIDE'
  | 'RIGHT_SIDE'
  | 'UPPER_BODY'
  | 'LOWER_BODY'
  | 'FULL_BODY';

export const BODY_PART_LABELS: Record<BodyPart, string> = {
  FRONT: 'Față',
  BACK: 'Spate',
  LEFT_SIDE: 'Lateral stânga',
  RIGHT_SIDE: 'Lateral dreapta',
  UPPER_BODY: 'Jumătate superioară',
  LOWER_BODY: 'Jumătate inferioară',
  FULL_BODY: 'Corp întreg',
};

export interface ProgressPhoto {
  id: string;
  userId: string;
  photoUrl: string;
  thumbnailUrl?: string;
  bodyPart: BodyPart;
  takenAt: string;
  weight?: number | null;
  bodyFat?: number | null;
  notes?: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface TimelineDay {
  date: string;
  count: number;
  photos: Partial<ProgressPhoto>[];
  thumbnail?: string;
  weight?: number | null;
}

export interface ProgressStats {
  totalPhotos: number;
  firstPhotoDate: string | null;
  latestPhotoDate: string | null;
  weightTrend: { date: string; weight: number | null }[];
  bodyFatTrend: { date: string; bodyFat: number | null }[];
  photosByBodyPart: Record<string, number>;
}

export interface UploadPhotoParams {
  file: File;
  bodyPart?: BodyPart;
  takenAt?: string;
  weight?: number;
  bodyFat?: number;
  notes?: string;
  tags?: string[];
}

const progressPhotosService = {
  /**
   * Upload a new progress photo
   */
  async uploadPhoto(params: UploadPhotoParams): Promise<{ success: boolean; photo: ProgressPhoto }> {
    const formData = new FormData();
    formData.append('photo', params.file);
    if (params.bodyPart) formData.append('bodyPart', params.bodyPart);
    if (params.takenAt) formData.append('takenAt', params.takenAt);
    if (params.weight !== undefined) formData.append('weight', String(params.weight));
    if (params.bodyFat !== undefined) formData.append('bodyFat', String(params.bodyFat));
    if (params.notes) formData.append('notes', params.notes);
    if (params.tags?.length) formData.append('tags', JSON.stringify(params.tags));

    const res = await fetch(`${API_URL}/api/progress-photos/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la încărcarea fotografiei');
    }
    return res.json();
  },

  /**
   * Get all photos with optional filters
   */
  async getPhotos(params?: {
    bodyPart?: BodyPart;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ photos: ProgressPhoto[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.bodyPart) query.set('bodyPart', params.bodyPart);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));

    const res = await fetch(`${API_URL}/api/progress-photos?${query}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Eroare la încărcarea fotografiilor');
    return res.json();
  },

  /**
   * Get timeline (grouped by date)
   */
  async getTimeline(): Promise<{ timeline: TimelineDay[]; totalPhotos: number }> {
    const res = await fetch(`${API_URL}/api/progress-photos/timeline`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Eroare la încărcarea timeline-ului');
    return res.json();
  },

  /**
   * Get progress stats
   */
  async getStats(): Promise<ProgressStats> {
    const res = await fetch(`${API_URL}/api/progress-photos/stats`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Eroare la încărcarea statisticilor');
    return res.json();
  },

  /**
   * Get two photos for comparison
   */
  async comparePhotos(photo1Id: string, photo2Id: string): Promise<{ photo1: ProgressPhoto; photo2: ProgressPhoto }> {
    const res = await fetch(
      `${API_URL}/api/progress-photos/compare?photo1=${photo1Id}&photo2=${photo2Id}`,
      { headers: getAuthHeaders() }
    );
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la compararea fotografiilor');
    }
    return res.json();
  },

  /**
   * Get single photo
   */
  async getPhoto(id: string): Promise<ProgressPhoto> {
    const res = await fetch(`${API_URL}/api/progress-photos/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Fotografia nu a fost găsită');
    return res.json();
  },

  /**
   * Update photo metadata
   */
  async updatePhoto(
    id: string,
    data: { notes?: string; tags?: string[]; weight?: number; bodyFat?: number; bodyPart?: BodyPart }
  ): Promise<ProgressPhoto> {
    const res = await fetch(`${API_URL}/api/progress-photos/${id}`, {
      method: 'PATCH',
      headers: getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la actualizarea fotografiei');
    }
    return res.json();
  },

  /**
   * Delete a photo
   */
  async deletePhoto(id: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_URL}/api/progress-photos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la ștergerea fotografiei');
    }
    return res.json();
  },

  /**
   * Build full URL for a photo
   */
  getPhotoUrl(photoUrl: string): string {
    if (!photoUrl) return '';
    if (photoUrl.startsWith('http')) return photoUrl;
    return `${API_URL}${photoUrl}`;
  },
};

export default progressPhotosService;
